import { FC } from 'react';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { useFormContext } from 'react-hook-form';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { enteredPercentageViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { percentageFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { percentageValidator, getRequiredValidator } from '@core/validations/hook-forms/validators';
import { ForecastInputDto } from '@app/shared/models/contracts/project-dto';
import { PredefinedVariableNames } from '@app/shared/models/contracts/enums/shared-enums';
import { useParams } from 'react-router-dom';
import {
  numberValueFormatter,
  percentageValueFormatterProps,
  getCalculatedProjectValueInPercent,
} from '@app/shared/formatters';
import { GridFieldTooltipVariation } from '@app/shared/components/grid-controls/grid-field-options';
import Button from '@app/shared/components/button/Button';
import SvgFilledRightArrow from '@app/shared/icons/FilledRightArrow';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { Alignment } from '@app/shared/enums/alignment.enum';
import styles from '../../deal-thesis-page.module.scss';
import { getDealThesisInputCaseForecastDate } from '@app/core/store/pwerm2-calculation-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import { DealThesisSpacerRow2 } from '../DealThesisSpacerRow2';
import DealThesisEmptyHistoricDataColumns from '../../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import useDealThesisData from '../../deal-thesis-utils/useDealThesisData';
import { DealThesisFormModel } from '../../DealThesisNavigation';

interface ExitCostsPartialTableProps {
  handleDataSubmit: () => void;
}

const ExitCostsPartialTable2: FC<ExitCostsPartialTableProps> = ({
  handleDataSubmit,
}): JSX.Element => {
  const { l } = useLocale();
  const { caseId } = useParams();
  const formMethods = useFormContext<DealThesisFormModel>();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);

  const {
    showHistoricDataColumns,
    caseResultsForecastDates,
    historicDataForecastYearsResults,
    getMaxColspan,
  } = useDealThesisData(caseId);

  const getTransactionCostOfEvAndIPODiscountOfEvSumMessage = (forecastId: string) => {
    const value1 = getCalculatedProjectValueInPercent(
      getDealThesisInputCaseForecastDate(
        results,
        caseId!,
        forecastId,
        (forecast) => forecast.weightedAvgTransactionCostsPc
      )
    );
    const value2 = getCalculatedProjectValueInPercent(
      getDealThesisInputCaseForecastDate(
        results,
        caseId!,
        forecastId,
        (forecast) => forecast.weightedIpoDiscountPc
      )
    );
    const sumOfProperties = Number(value1) + Number(value2);
    return sumOfProperties > 100
      ? `${PredefinedVariableNames.TransactionCostsOfEV} and ${
          PredefinedVariableNames.IPODiscountOfEV
        } add up to ${numberValueFormatter({
          value: sumOfProperties,
          ...percentageValueFormatterProps,
        })}, this must be less than ${numberValueFormatter({
          value: 100,
          ...percentageValueFormatterProps,
        })}.`
      : undefined;
  };

  const forecastYears = caseResultsForecastDates.length ?? 0;

  const populate = (inputField: string, cellIndex: number) => {
    const { ...updatedDealThesisInput } = formMethods.getValues();
    updatedDealThesisInput.forecasts.map((_, index: number) => {
      index >= cellIndex &&
        formMethods.setValue(
          `forecasts.${index}.inputs.${inputField as keyof ForecastInputDto}`,
          updatedDealThesisInput.forecasts[cellIndex].inputs[inputField as keyof ForecastInputDto]
        );
    });

    handleDataSubmit();
  };

  const shouldAutoPopulateFromCell = (index: number): boolean | undefined =>
    index !== forecastYears - 1;

  return (
    <>
      <tbody>
        {caseId && caseResultsForecastDates && (
          <>
            <tr>
              <th className="table-primary__cell--header">{l('_ExitCostsTitle')}</th>
              <td colSpan={getMaxColspan() - 1} className="table-primary__cell--header" />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator">{l('_ProbabilityOfIpo')}</th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((_, index) => (
                <td key={index + 'propability-ipo'} className={styles['hoverable-cell']}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.probabilityOfIpo`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('probabilityOfIpo', index)}
                      data-testid={`populate-probabilityOfIpo-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator">
                {l('_ProbabilityOfSecondaryTradeSale')}
              </th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((forecast, index) => (
                <td key={index + 'trade-sale'}>
                  <CellValue
                    {...enteredPercentageViewRules}
                    value={getCalculatedProjectValueInPercent(
                      getDealThesisInputCaseForecastDate(
                        results,
                        caseId!,
                        forecast.forecastId,
                        (forecast) => forecast.secondarySaleProbabilityPc
                      )
                    )}
                  />
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator">
                {l('_IPOTransactionCostsTitle')}
              </th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((_, index) => (
                <td key={index + 'ipo-transactions'} className={styles['hoverable-cell']}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.ipoTransactionCosts`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('ipoTransactionCosts', index)}
                      data-testid={`populate-ipoTransactionCosts-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator">
                {l('_SecondaryOrTradeSaleTransactionCost')}
              </th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((_, index) => (
                <td key={index + 'trade-sale-transactions'} className={styles['hoverable-cell']}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.tradeSaleTransactionCost`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('tradeSaleTransactionCost', index)}
                      data-testid={`populate-tradeSaleTransactionCost-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator">
                <strong>{l('_TransactionCostsPcOfEv')}</strong>
              </th>
              <td className="table-primary__cell--section-start-separator" />
              <DealThesisEmptyHistoricDataColumns
                isHistoricEventsSection={false}
                isStartSeparator
              />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-start-separator" />
                ))}
              {caseResultsForecastDates.map((forecast, index) => (
                <td
                  key={index + 'transaction-costs'}
                  className="table-primary__cell--section-start-separator">
                  <CellValue
                    {...enteredPercentageViewRules}
                    warningMessage={getTransactionCostOfEvAndIPODiscountOfEvSumMessage(
                      forecast.forecastId
                    )}
                    tooltipVariation={GridFieldTooltipVariation.FitContainer}
                    value={getCalculatedProjectValueInPercent(
                      getDealThesisInputCaseForecastDate(
                        results,
                        caseId!,
                        forecast.forecastId,
                        (forecast) => forecast.weightedAvgTransactionCostsPc
                      )
                    )}
                    strong
                  />
                </td>
              ))}
              <td className="table-primary__cell--section-start-separator" />
            </tr>
            <DealThesisSpacerRow2 />
            <tr>
              <th className="table-primary__cell--vertical-separator">{l('_IpoDiscount')}</th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((forecast, index) => (
                <td key={index + 'ipo-discount'} className={styles['hoverable-cell']}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.ipoDiscount`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('ipoDiscount', index)}
                      data-testid={`populate-ipoDiscount-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator">
                {l('_HypotheticalDebtAtIpo')}
              </th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((_, index) => (
                <td key={index + 'hypohotetical-debt'} className={styles['hoverable-cell']}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.hypotheticalDebtAtIpo`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('hypotheticalDebtAtIpo', index)}
                      data-testid={`populate-hypotheticalDebtAtIpo-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator">
                {l('_PercentageEquitySoldInIpo')}
              </th>
              <td />
              <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => <td key={index} />)}
              {caseResultsForecastDates.map((_, index) => (
                <td key={index + 'percent-of-equity'} className={styles['hoverable-cell']}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.percentOfEquitySoldInIpo`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('percentOfEquitySoldInIpo', index)}
                      data-testid={`populate-percentOfEquitySoldInIpo-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator">
                <strong>{l('_IPODiscountPercentOfEv')}</strong>
              </th>
              <td className="table-primary__cell--section-start-separator" />
              <DealThesisEmptyHistoricDataColumns
                isHistoricEventsSection={false}
                isStartSeparator
              />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-start-separator" />
                ))}
              {caseResultsForecastDates.map((forecast, index) => (
                <td
                  key={index + 'ipo-discount-of-ev'}
                  className="table-primary__cell--section-start-separator">
                  <CellValue
                    {...enteredPercentageViewRules}
                    warningMessage={getTransactionCostOfEvAndIPODiscountOfEvSumMessage(
                      forecast.forecastId
                    )}
                    tooltipVariation={GridFieldTooltipVariation.FitContainer}
                    strong
                    value={getCalculatedProjectValueInPercent(
                      getDealThesisInputCaseForecastDate(
                        results,
                        caseId!,
                        forecast.forecastId,
                        (forecast) => forecast.weightedIpoDiscountPc
                      )
                    )}
                  />
                </td>
              ))}
              <td className="table-primary__cell--section-start-separator" />
            </tr>
            <DealThesisSpacerRow2 />
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator">
                {l('_PostIPOLockInDiscount')}
              </th>
              <td className="table-primary__cell--section-start-separator" />
              <DealThesisEmptyHistoricDataColumns
                isHistoricEventsSection={false}
                isStartSeparator
              />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-start-separator" />
                ))}
              {caseResultsForecastDates.map((forecast, index) => (
                <td
                  key={index + 'post-ipo-sale-restrictions'}
                  className={`table-primary__cell--section-start-separator ${styles['hoverable-cell']}`}>
                  <GridTextField
                    className={[
                      shouldAutoPopulateFromCell(index) ? styles[`padding-right-hover`] : '',
                    ]}
                    name={`forecasts.${index}.inputs.dlomForPostIpoSaleRestrictions`}
                    {...percentageFieldFormattingProps}
                    rules={{
                      ...percentageValidator,
                      ...getRequiredValidator(),
                    }}
                    alignment={Alignment.Right}
                  />
                  {shouldAutoPopulateFromCell(index) && (
                    <Button
                      startIcon={<SvgFilledRightArrow />}
                      className={styles['deal-thesis-populate-button']}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.FLUID}
                      // stop the onBlur event occurring so we can control when the data is submitted
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => populate('dlomForPostIpoSaleRestrictions', index)}
                      data-testid={`populate-dlomForPostIpoSaleRestrictions-${index}`}
                      autoIconSize
                    />
                  )}
                </td>
              ))}
              <td className="table-primary__cell--section-start-separator" />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator">
                {l('_ImpliedEquityRetainedOnIPO')}
              </th>
              <td className="table-primary__cell--section-start-separator" />
              <DealThesisEmptyHistoricDataColumns
                isHistoricEventsSection={false}
                isStartSeparator
              />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-start-separator" />
                ))}
              {caseResultsForecastDates.map((forecast, index) => (
                <td
                  key={index + 'implied-equity-retained'}
                  className="table-primary__cell--section-start-separator">
                  <CellValue
                    {...enteredPercentageViewRules}
                    value={getCalculatedProjectValueInPercent(
                      getDealThesisInputCaseForecastDate(
                        results,
                        caseId!,
                        forecast.forecastId,
                        (forecast) => forecast.equityRetainedInIpoPc
                      )
                    )}
                  />
                </td>
              ))}
              <td className="table-primary__cell--section-start-separator" />
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator">
                <strong>{l('_ProbabilityWeightedPostIPOLockInDiscountTitle')}</strong>
              </th>
              <td className="table-primary__cell--section-start-separator" />
              <DealThesisEmptyHistoricDataColumns
                isHistoricEventsSection={false}
                isStartSeparator
              />
              {showHistoricDataColumns &&
                historicDataForecastYearsResults.map((_, index) => (
                  <td key={index} className="table-primary__cell--section-start-separator" />
                ))}
              {caseResultsForecastDates.map((forecast, index) => (
                <td
                  key={index + 'prop-weighted'}
                  className="table-primary__cell--section-start-separator">
                  <CellValue
                    {...enteredPercentageViewRules}
                    strong
                    value={getCalculatedProjectValueInPercent(
                      getDealThesisInputCaseForecastDate(
                        results,
                        caseId!,
                        forecast.forecastId,
                        (forecast) => forecast.weightedPostIpoSaleRestrictionDlomPc
                      )
                    )}
                  />
                </td>
              ))}
              <td className="table-primary__cell--section-start-separator" />
            </tr>
            <DealThesisSpacerRow2 />
          </>
        )}
      </tbody>
    </>
  );
};

export default ExitCostsPartialTable2;
